import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { buildSplitRows } from '../../utils/putawayHelper';
import { putawayService } from '../../services/api';
const AssignBinPopUp = ({
  open = false,
  onClose,
  onApply,
  putawayTask = null,
  bins: binsProp = [],
  loading = false,
}) => {
  const [rowConditions, setRowConditions] = useState([]);
  const [binsByCondition, setBinsByCondition] = useState({ GOOD: [], DAMAGED: [] });
  const [binsLoadingByCondition, setBinsLoadingByCondition] = useState({ GOOD: false, DAMAGED: false });
  const [binSelections, setBinSelections] = useState([]);
  const [rowQtys, setRowQtys] = useState([]);
  const [rowSearches, setRowSearches] = useState([]); // per-row search (isolated state)
  const [openBinDropdownRow, setOpenBinDropdownRow] = useState(null);
  const binDropdownContainerRef = useRef(null);

  const task = putawayTask || null;
  const isReturnTask = String(task?.transactionType || '').toUpperCase() === 'RETURN';
  const product =
    (task?.product && typeof task.product === 'object' ? task.product : null) ||
    (task?.productId && typeof task.productId === 'object' ? task.productId : null);
  const productSku = product?.sku ?? (task?.productId ? String(task.productId) : 'N/A');
  const productDesc = product?.description ?? '';
  const conversionPerPallet = product?.conversionQtyPerPallet != null && product?.conversionQtyPerPallet !== ''
    ? Number(product.conversionQtyPerPallet)
    : (task?.qtyPerBin != null && task?.qtyPerBin !== '' ? Number(task.qtyPerBin) : null);

  const invoiceQty = task?.invoiceQuantity != null && task?.invoiceQuantity !== '' ? Number(task.invoiceQuantity) : null;
  const receivedQty = task?.receivedQuantity != null && task?.receivedQuantity !== '' ? Number(task.receivedQuantity) : null;
  const assignedQty = task?.assignedQuantity != null && task?.assignedQuantity !== '' ? Number(task.assignedQuantity) : 0;
  const remainingQty = task?.remainingQuantity != null && task?.remainingQuantity !== '' ? Number(task.remainingQuantity) : null;
  const qtyPerBinNum = task?.qtyPerBin != null && task?.qtyPerBin !== '' ? Number(task.qtyPerBin) : null;
  const suggestedBinsBackend = task?.suggestedBins != null && task?.suggestedBins !== '' ? Number(task.suggestedBins) : null;
  const suggestedBinsDisplay =
    Number.isFinite(suggestedBinsBackend) && suggestedBinsBackend >= 0
      ? suggestedBinsBackend
      : (Number.isFinite(remainingQty) && Number.isFinite(qtyPerBinNum) && qtyPerBinNum > 0
          ? Math.ceil(remainingQty / qtyPerBinNum)
          : Number.isFinite(receivedQty) && Number.isFinite(qtyPerBinNum) && qtyPerBinNum > 0
            ? Math.ceil(receivedQty / qtyPerBinNum)
            : null);

  const existingAssignments = useMemo(() => {
    const list = task?.assignments ?? task?.binAssignments ?? [];
    return Array.isArray(list) ? list : [];
  }, [task?.assignments, task?.binAssignments]);

  const hasExisting = existingAssignments.length > 0;
  const canGenerateNew = Number.isFinite(remainingQty) && remainingQty > 0 && Number.isFinite(qtyPerBinNum) && qtyPerBinNum > 0;

  // OLD: split rows were always built from remainingQty only.
  // This could get out of sync with the suggested bins summary when remainingQty
  // was stale but receivedQty / suggestedBins had already been updated.
  // const splitRowsNew = useMemo(() => {
  //   if (!canGenerateNew) return [];
  //   return buildSplitRows(remainingQty, qtyPerBinNum);
  // }, [canGenerateNew, remainingQty, qtyPerBinNum]);

  // NEW: build suggested split rows from the latest quantity snapshot:
  // - Prefer remainingQty when valid (> 0)
  // - Fallback to receivedQty (matches summary + suggested bins)
  // const splitRowsNew = useMemo(() => {
  //   if (!canGenerateNew) return [];
  //   const qtySource =
  //     (Number.isFinite(remainingQty) && remainingQty > 0)
  //       ? remainingQty
  //       : (Number.isFinite(receivedQty) ? receivedQty : 0);
  //   if (!qtySource || !Number.isFinite(qtySource) || !Number.isFinite(qtyPerBinNum) || qtyPerBinNum <= 0) {
  //     return [];
  //   }
  //   return buildSplitRows(qtySource, qtyPerBinNum);
  // }, [canGenerateNew, remainingQty, receivedQty, qtyPerBinNum]);
  const splitRowsNew = useMemo(() => {
    if (!Number.isFinite(remainingQty) || remainingQty <= 0) {
      return [];
    }
    if (isReturnTask) {
      return [{ qty: remainingQty }];
    }
    if (!Number.isFinite(qtyPerBinNum) || qtyPerBinNum <= 0) {
      return [];
    }
    return buildSplitRows(remainingQty, qtyPerBinNum);
  }, [isReturnTask, remainingQty, qtyPerBinNum]);
  const rows = useMemo(() => {
    if (hasExisting) {
      return existingAssignments.map((a) => {
        const bid = a.binId?._id ?? a.binId;
        const binIdStr = bid != null ? (typeof bid === 'string' ? bid : (bid.toString?.() || String(bid))) : '';
        return {
          qty: Number(a.assignedQty) || 0,
          binId: binIdStr,
          condition: (a.condition || '').toUpperCase() || 'GOOD',
        };
      });
    }
    return splitRowsNew.map((r, i) => ({
      qty: r.qty,
      binId: '',
      condition: (rowConditions[i] || '').toUpperCase() || '',
    }));
  }, [hasExisting, existingAssignments, splitRowsNew, rowConditions]);

  useEffect(() => {
    if (!open) {
      setRowConditions([]);
      setBinsByCondition({ GOOD: [], DAMAGED: [] });
      setBinSelections([]);
      setRowQtys([]);
      setRowSearches([]);
      setOpenBinDropdownRow(null);
      return;
    }
    document.body.style.overflow = 'hidden';
    if (hasExisting && existingAssignments.length > 0) {
      const conds = existingAssignments.map((a) => (a.condition || '').toUpperCase() || 'GOOD');
      setRowConditions(conds);
      const binIds = existingAssignments.map((a) => {
        const bid = a.binId?._id ?? a.binId;
        return bid != null ? (typeof bid === 'string' ? bid : String(bid)) : '';
      });
      setBinSelections(binIds);
      setRowQtys(existingAssignments.map((a) => Number(a.assignedQty) || 0));
      setRowSearches(conds.map(() => ''));
      conds.forEach((c) => { if (c === 'GOOD' || c === 'DAMAGED') fetchBinsForCondition(c); });
    } else if (splitRowsNew.length > 0) {
      setRowConditions(splitRowsNew.map(() => ''));
      setBinSelections(splitRowsNew.map(() => ''));
      setRowQtys(splitRowsNew.map((r) => r.qty));
      setRowSearches(splitRowsNew.map(() => ''));
    }
    return () => { document.body.style.overflow = ''; };
  }, [open, hasExisting, existingAssignments.length, splitRowsNew.length]);

  // Keep row state arrays in sync with row count (per-row isolation: condition, bin, search per row)
  useEffect(() => {
    if (!open) return;
    const targetLen = hasExisting ? existingAssignments.length : splitRowsNew.length;
    if (targetLen <= 0) return;
    setRowConditions((prev) => {
      if (prev.length === targetLen) return prev;
      const next = prev.slice(0, targetLen);
      while (next.length < targetLen) next.push('');
      return next;
    });
    setBinSelections((prev) => {
      if (prev.length === targetLen) return prev;
      const next = prev.slice(0, targetLen);
      while (next.length < targetLen) next.push('');
      return next;
    });
    setRowSearches((prev) => {
      if (prev.length === targetLen) return prev;
      const next = prev.slice(0, targetLen);
      while (next.length < targetLen) next.push('');
      return next;
    });
  }, [open, hasExisting, existingAssignments.length, splitRowsNew.length]);

  const fetchBinsForCondition = (cond) => {
    if (!task?._id || (cond !== 'GOOD' && cond !== 'DAMAGED')) return;
    setBinsLoadingByCondition((prev) => ({ ...prev, [cond]: true }));
    putawayService
      .getBinsAvailableForPutaway(task._id, { condition: cond })
      .then((res) => {
        const list = res?.data ?? [];
        setBinsByCondition((prev) => ({ ...prev, [cond]: Array.isArray(list) ? list : [] }));
      })
      .catch(() => setBinsByCondition((prev) => ({ ...prev, [cond]: [] })))
      .finally(() => setBinsLoadingByCondition((prev) => ({ ...prev, [cond]: false })));
  };

  const setRowConditionAt = (index, value) => {
    const cond = (value || '').toUpperCase();
    setRowConditions((prev) => {
      const next = [...prev];
      next[index] = cond;
      return next;
    });
    // Clear bin for this row when condition changes (bin list is condition-specific)
    setBinSelections((prev) => {
      const next = [...prev];
      next[index] = '';
      return next;
    });
    // Reset dropdown + search for this row when condition changes
    setOpenBinDropdownRow((prev) => (prev === index ? null : prev));
    setRowSearches((prev) => {
      const next = [...prev];
      next[index] = '';
      return next;
    });
    if (cond === 'GOOD' || cond === 'DAMAGED') fetchBinsForCondition(cond);
  };

  const setRowSearchAt = (index, value) => {
    setRowSearches((prev) => {
      const next = [...prev];
      next[index] = value ?? '';
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  useEffect(() => {
    if (openBinDropdownRow == null) return;
    const handleClickOutside = (e) => {
      if (binDropdownContainerRef.current && !binDropdownContainerRef.current.contains(e.target)) {
        const rowIndex = openBinDropdownRow;
        setOpenBinDropdownRow(null);
        setRowSearches((prev) => {
          const next = [...prev];
          next[rowIndex] = '';
          return next;
        });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openBinDropdownRow]);

  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  const setBinAt = (index, binId) => {
    setBinSelections((prev) => {
      const next = [...prev];
      next[index] = binId;
      return next;
    });
  };

  const effectiveRows = hasExisting ? rows : splitRowsNew;

  const assignmentRows = useMemo(
    () =>
      effectiveRows.map((_, idx) => ({
        binId: binSelections[idx] || '',
        assignedQty: Number(effectiveRows[idx]?.qty ?? rowQtys[idx] ?? 0),
      })),
    [effectiveRows, binSelections, rowQtys]
  );

  const getRemainingCapacity = (bin, currentRowIndex) => {
    // Capacity validation disabled — bins have unlimited capacity.
    // const totalCapacity = Number(bin.capacity || 0);
    // const usedQty = Number(bin.used ?? bin.currentQuantity ?? 0);
    //
    // const tempAssignedQty = assignmentRows.reduce((sum, row, idx) => {
    //   if (idx === currentRowIndex) return sum;
    //
    //   if (String(row.binId) === String(bin._id)) {
    //     return sum + Number(row.assignedQty || 0);
    //   }
    //
    //   return sum;
    // }, 0);
    //
    // return Math.max(totalCapacity - usedQty - tempAssignedQty, 0);
    return Infinity;
  };

  const allRowsHaveBin = effectiveRows.length > 0 && binSelections.length === effectiveRows.length && binSelections.every((id) => id);
  const allRowsHaveCondition = effectiveRows.length > 0 && rowConditions.length >= effectiveRows.length &&
    effectiveRows.every((_, i) => rowConditions[i] === 'GOOD' || rowConditions[i] === 'DAMAGED');
  const canSave = effectiveRows.length > 0 && allRowsHaveBin && allRowsHaveCondition;

  const handleApply = () => {
    console.log('[AssignBinPopUp] Save button clicked');
    if (!onApply) {
      console.log('[AssignBinPopUp] Validation failure: onApply is not defined, returning');
      return;
    }
    if (!canSave) {
      console.log('[AssignBinPopUp] Validation failure: canSave is false', {
        effectiveRowsLength: effectiveRows.length,
        allRowsHaveBin,
        allRowsHaveCondition,
        rowConditionsLength: rowConditions.length,
        binSelectionsLength: binSelections.length,
      });
      return;
    }
  if (!Number.isFinite(remainingQty) || remainingQty <= 0) {
   console.log('[AssignBinPopUp] Blocked: No remaining quantity');
   return;
 }
    const assignments = effectiveRows.map((_, i) => {
      const condition = (rowConditions[i] || 'GOOD').toUpperCase();
      const item = {
        binId: binSelections[i],
        assignedQty: effectiveRows[i]?.qty ?? rowQtys[i] ?? 0,
        condition,
      };
      return {
        ...item,
        ...(condition === 'GOOD' ? { status: 'APPROVED' } : {}),
      };
    }).filter((a) => a.assignedQty > 0);
    const payload = { assignments };
    console.log('[AssignBinPopUp] Current assignment state', {
      effectiveRows,
      binSelections,
      rowConditions,
      rowQtys,
    });
    console.log('[AssignBinPopUp] Final payload before API call', payload);
    onApply(payload);
  };

  if (!open || !task) return null;

  const num = (v) => (Number.isFinite(v) ? v : '—');
  const showTable = hasExisting || splitRowsNew.length > 0;

  const popupContent = (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }} role="dialog" aria-modal="true" aria-labelledby="assign-bin-popup-title">
      <div className="absolute inset-0 bg-black/50" onClick={handleBackdropClick} aria-hidden="true" />
      <div className="relative w-full max-w-[1400px] mx-4 bg-white rounded-2xl shadow-xl overflow-visible h-[96vh] flex flex-col"
      
      onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-4">
          <h3 id="assign-bin-popup-title" className="text-lg font-semibold text-gray-900">Assign Bins to Putaway</h3>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-200" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-6 pb-2 overflow-y-auto flex-1 min-h-0">
          <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs font-semibold text-gray-700 mb-2">Putaway details</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
              <div><strong>Putaway ID:</strong> {task.putawayId ?? '—'}</div>
              <div><strong>Transaction:</strong> {task.transactionId ?? '—'}</div>
              <div className="col-span-2"><strong>Product:</strong> {productSku}{productDesc ? ` — ${productDesc}` : ''}</div>
            </div>
          </div>

          <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
            <p className="text-xs font-semibold text-gray-700 mb-2">Quantity summary</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm text-gray-700">
              <div><strong>Invoice Qty:</strong> {num(invoiceQty)}</div>
              <div><strong>Received Qty:</strong> {num(receivedQty)}</div>
              <div><strong>Assigned Qty:</strong> {num(assignedQty)}</div>
              <div><strong>Remaining Qty:</strong> {num(remainingQty)}</div>
              <div><strong>Conversion per pallet (Product Master):</strong> {num(conversionPerPallet)}</div>
              <div><strong>Qty Per Bin:</strong> {num(qtyPerBinNum)}</div>
              <div><strong>Suggested Bins:</strong> {num(suggestedBinsDisplay)}</div>
            </div>
          </div>

          {/* {!canGenerateNew && !hasExisting && ( */}
          {(!Number.isFinite(remainingQty) || remainingQty <= 0) && !hasExisting && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No remaining quantity or invalid qty per bin. You can still open this popup to view or edit existing assignments.
            </p>
          )}

          {showTable && (
            <>
              <p className="text-sm text-gray-700 mb-2">
                Each row has its own condition (GOOD = GOOD bins, DAMAGED = HOLD bins). The same bin can be used on multiple rows.
                {/* Capacity restriction disabled: until its capacity is full. */}
              </p>
              <div className="border border-gray-200 rounded-lg overflow-x-auto relative z-[1000]">
                <div className="max-h-[45vh] overflow-y-auto overflow-x-visible relative pb-40">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">#</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-700">Assigned Qty</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Condition</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Bin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {effectiveRows.map((row, i) => {
                      const rowCond = (rowConditions[i] || '').toString().trim().toUpperCase();
                      const hasCondition = rowCond === 'GOOD' || rowCond === 'DAMAGED';
                      // GOOD → only GOOD bins; DAMAGED → only HOLD bins (from API)
                      const binsForRow = hasCondition ? (binsByCondition[rowCond] || []) : [];
                      const rowBinsLoading = rowCond === 'GOOD' ? binsLoadingByCondition.GOOD : rowCond === 'DAMAGED' ? binsLoadingByCondition.DAMAGED : false;
                      const rowSearch = (rowSearches[i] ?? '').toString();
                      return (
                        <tr key={`row-${i}-${rowCond}`} className={`border-b border-gray-100 last:border-b-0 relative ${
                            openBinDropdownRow === i ? 'z-[2000]' : 'z-0'
                          }`}>
                          <td className="py-2 px-3 text-gray-600">{i + 1}</td>
                          <td className="py-2 px-3 text-right font-medium">{row.qty ?? (splitRowsNew[i]?.qty ?? 0)}</td>
                          <td className="py-2 px-3">
                            <select
                              value={rowCond}
                              onChange={(e) => setRowConditionAt(i, e.target.value)}
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                            >
                              <option value="">-- Condition --</option>
                              <option value="GOOD">GOOD (GOOD bins)</option>
                              <option value="DAMAGED">DAMAGED (HOLD bins)</option>
                            </select>
                          </td>
                          <td className="py-2 px-3 relative z-[2001]"> 
                            {(() => {
                              const filteredBins = binsForRow.filter((bin) =>
                                bin.binCode?.toLowerCase().includes(rowSearch.toLowerCase())
                              );
                              const selectedBin = binsForRow.find((b) => b._id === binSelections[i]);
                              const displayLabel = selectedBin
                                ? selectedBin.binCode + ' (' + (selectedBin.binType || '—') + ')'
                                : '';
                              const isOpen = openBinDropdownRow === i;
                              const placeholder = hasCondition ? 'Search bin...' : 'Select condition first';
                              const binInputDisabled = rowBinsLoading || !hasCondition;
                              return (
                                <div className="relative w-full" ref={isOpen ? binDropdownContainerRef : undefined}>
                                  <input
                                    type="text"
                                    value={isOpen ? rowSearch : displayLabel}
                                    onFocus={() => {
                                      if (!hasCondition) return;
                                      if (rowBinsLoading) return;
                                      setOpenBinDropdownRow(i);
                                      setRowSearchAt(i, '');
                                    }}
                                    onChange={(e) => {
                                      setRowSearchAt(i, e.target.value);
                                      setOpenBinDropdownRow(i);
                                    }}
                                    placeholder={placeholder}
                                    disabled={binInputDisabled}
                                    className="relative z-10 bg-white w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  />
                                  {isOpen && hasCondition && (
                                    <div className="absolute left-0 right-0 mt-1 z-[5000] bg-white border border-gray-300 rounded-lg shadow-lg min-w-[200px]">
                                      <div className="max-h-40 overflow-y-auto">
                                        {filteredBins.length === 0 && (
                                          <div className="px-3 py-2 text-gray-500">No bins found</div>
                                        )}
                                        {filteredBins.map((bin) => {
                                          // const requiredQty = Number(
                                          //   row.qty ?? rowQtys[i] ?? row.assignedQty ?? 0
                                          // );
                                          // const remaining = getRemainingCapacity(bin, i);
                                          // const canAssign = remaining >= requiredQty;
                                          // const label = canAssign
                                          //   ? `${bin.binCode} (${bin.binType || '—'}) — ${remaining} left`
                                          //   : `${bin.binCode} (${bin.binType || '—'}) — Need ${requiredQty} • Left ${remaining}`;
                                          // const disabled = !canAssign;
                                          const label = `${bin.binCode} (${bin.binType || '—'})`;
                                          const disabled = false;
                                          return (
                                            <div
                                              key={bin._id}
                                              className={`px-3 py-2 text-sm ${
                                                disabled
                                                  ? 'text-red-400 cursor-not-allowed'
                                                  : 'text-gray-700 cursor-pointer hover:bg-gray-100'
                                              }`}
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                if (disabled) return;
                                                setBinAt(i, bin._id);
                                                setOpenBinDropdownRow(null);
                                                setRowSearchAt(i, '');
                                              }}
                                            >
                                              {label}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="relative z-10 flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-200">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={loading || !canSave}
            className="h-10 rounded-full border border-emerald-500 bg-emerald-500 px-5 text-sm font-medium text-white hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default AssignBinPopUp;
